import React, { useState, useEffect } from 'react';
import { TrendingUp, Calendar, Award, ChevronLeft, ChevronRight, Download, FileText, Loader2 } from 'lucide-react';
import * as Icons from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import { Button } from '../components/ui/button';
import { getHabits, getLogs, calculateStreak, calculateCompletionRate } from '../lib/storage';
import { exportAPI } from '../services/api';
import { toast } from 'sonner';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const Analytics = () => {
  const [habits, setHabits] = useState([]);
  const [allLogs, setAllLogs] = useState([]);
  const [analytics, setAnalytics] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [exportingPDF, setExportingPDF] = useState(false);
  const [viewMode, setViewMode] = useState('weekly'); // 'weekly' or 'monthly'
  const [offset, setOffset] = useState(0); // 0 = current period, 1 = previous period, etc.

  useEffect(() => {
    loadAnalytics();
  }, []);

  useEffect(() => {
    if (allLogs.length > 0 || habits.length > 0) {
      generateChartData(habits, allLogs);
    }
  }, [viewMode, offset, allLogs, habits]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const [loadedHabits, loadedLogs] = await Promise.all([
        getHabits(),
        getLogs()
      ]);
      
      setHabits(loadedHabits || []);
      setAllLogs(loadedLogs || []);

      // Calculate analytics for each habit
      const habitAnalytics = (loadedHabits || []).map((habit) => ({
        ...habit,
        streak: calculateStreak(habit.id, loadedLogs || []),
        completionRate: calculateCompletionRate(habit.id, loadedLogs || [], 30),
      }));

      setAnalytics(habitAnalytics);
      generateChartData(loadedHabits || [], loadedLogs || []);
    } catch (error) {
      console.error('Error loading analytics:', error);
      setHabits([]);
      setAnalytics([]);
      setChartData([]);
    } finally {
      setLoading(false);
    }
  };

  const generateChartData = (habits, logs) => {
    const data = [];
    const today = new Date();
    
    // Helper function to format date in local timezone (YYYY-MM-DD)
    const formatDateLocal = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    if (viewMode === 'weekly') {
      // Weekly view: 7 days
      const startOffset = offset * 7;
      for (let i = 6; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(date.getDate() - i - startOffset);
        const dateStr = formatDateLocal(date);

        const completions = logs.filter((log) => log.date === dateStr).length;

        data.push({
          label: date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
          shortLabel: date.toLocaleDateString('en-US', { weekday: 'short' }),
          completions,
          date: dateStr,
        });
      }
    } else {
      // Monthly view: ~30 days grouped by week or shown as daily
      const startOffset = offset * 30;
      for (let i = 29; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(date.getDate() - i - startOffset);
        const dateStr = formatDateLocal(date);

        const completions = logs.filter((log) => log.date === dateStr).length;

        data.push({
          label: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          shortLabel: date.getDate().toString(),
          completions,
          date: dateStr,
        });
      }
    }

    setChartData(data);
  };

  const getPeriodLabel = () => {
    const today = new Date();
    if (viewMode === 'weekly') {
      const startDate = new Date(today);
      startDate.setDate(startDate.getDate() - 6 - (offset * 7));
      const endDate = new Date(today);
      endDate.setDate(endDate.getDate() - (offset * 7));
      
      if (offset === 0) return 'This Week';
      if (offset === 1) return 'Last Week';
      return `${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
    } else {
      const startDate = new Date(today);
      startDate.setDate(startDate.getDate() - 29 - (offset * 30));
      const endDate = new Date(today);
      endDate.setDate(endDate.getDate() - (offset * 30));
      
      if (offset === 0) return 'Last 30 Days';
      return `${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
    }
  };

  const handlePrevious = () => setOffset(prev => prev + 1);
  const handleNext = () => setOffset(prev => Math.max(0, prev - 1));

  const handleExportCSV = async () => {
    try {
      setExporting(true);
      const blob = await exportAPI.downloadCSV();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `habit2goals_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Habits exported successfully!');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export habits');
    } finally {
      setExporting(false);
    }
  };

  const handleExportPDF = async () => {
    try {
      setExportingPDF(true);
      
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      
      // Title
      doc.setFontSize(24);
      doc.setTextColor(59, 130, 246); // Blue color
      doc.text('Habit2Goals', pageWidth / 2, 20, { align: 'center' });
      
      // Subtitle
      doc.setFontSize(12);
      doc.setTextColor(100, 100, 100);
      doc.text('Habit Analytics Report', pageWidth / 2, 28, { align: 'center' });
      doc.text(`Generated on ${new Date().toLocaleDateString('en-US', { 
        year: 'numeric', month: 'long', day: 'numeric' 
      })}`, pageWidth / 2, 34, { align: 'center' });
      
      // Summary Stats
      doc.setFontSize(14);
      doc.setTextColor(0, 0, 0);
      doc.text('Summary', 14, 48);
      
      const totalHabits = analytics.length;
      const totalCompletions = allLogs.length;
      const avgCompletionRate = analytics.length > 0 
        ? Math.round(analytics.reduce((sum, h) => sum + h.completionRate, 0) / analytics.length)
        : 0;
      const bestStreak = analytics.length > 0 
        ? Math.max(...analytics.map(h => h.streak))
        : 0;
      
      doc.setFontSize(10);
      doc.text(`Total Habits: ${totalHabits}`, 14, 56);
      doc.text(`Total Completions: ${totalCompletions}`, 14, 62);
      doc.text(`Average Completion Rate: ${avgCompletionRate}%`, 14, 68);
      doc.text(`Best Current Streak: ${bestStreak} days`, 14, 74);
      
      // Habits Table
      if (analytics.length > 0) {
        doc.setFontSize(14);
        doc.text('Habit Details', 14, 88);
        
        const tableData = analytics.map(habit => [
          habit.name,
          habit.category || 'General',
          `${habit.streak} days`,
          `${habit.completionRate}%`,
        ]);
        
        autoTable(doc, {
          startY: 92,
          head: [['Habit Name', 'Category', 'Current Streak', 'Completion Rate']],
          body: tableData,
          theme: 'striped',
          headStyles: { 
            fillColor: [59, 130, 246],
            textColor: 255,
            fontStyle: 'bold'
          },
          alternateRowStyles: { fillColor: [240, 245, 255] },
          styles: { fontSize: 9 },
        });
      }
      
      // Footer
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(
          'Habit2Goals - Build Better Habits, Achieve Your Goals',
          pageWidth / 2,
          doc.internal.pageSize.getHeight() - 10,
          { align: 'center' }
        );
      }
      
      // Save
      doc.save(`habit2goals_report_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success('PDF report exported successfully!');
      
    } catch (error) {
      console.error('PDF Export error:', error);
      toast.error('Failed to export PDF');
    } finally {
      setExportingPDF(false);
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="gradient-card p-3 rounded-lg border border-white/20 shadow-lg">
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">{payload[0]?.payload?.label}</p>
          <p className="text-sm font-medium text-slate-800 dark:text-slate-100">
            {payload[0].value} completions
          </p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600 dark:text-slate-400">Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="analytics-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h2 className="font-heading text-4xl md:text-5xl font-bold gradient-text">
          Analytics
        </h2>
        <div className="flex gap-3">
          <Button
            onClick={handleExportPDF}
            disabled={exportingPDF || habits.length === 0}
            className="gap-2 bg-gradient-to-r from-purple-500 to-violet-600 hover:from-purple-600 hover:to-violet-700 text-white shadow-lg"
            data-testid="export-pdf-button"
          >
            {exportingPDF ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <Download className="w-4 h-4" />
                Export PDF
              </>
            )}
          </Button>
          <Button
            onClick={handleExportCSV}
            disabled={exporting || habits.length === 0}
            className="gap-2 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white shadow-lg"
            data-testid="export-csv-button"
          >
            {exporting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <Download className="w-4 h-4" />
                Export CSV
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Activity Chart with View Controls */}
      <div className="gradient-card rounded-2xl border border-white/20 dark:border-white/10 p-6 shadow-xl">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            <h3 className="font-heading text-xl font-semibold text-slate-800 dark:text-slate-100">
              Activity Overview
            </h3>
          </div>
          
          {/* View Mode Toggle */}
          <div className="flex items-center gap-2">
            <div className="flex bg-slate-100 dark:bg-slate-800 rounded-lg p-1">
              <button
                onClick={() => { setViewMode('weekly'); setOffset(0); }}
                data-testid="view-weekly-btn"
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                  viewMode === 'weekly' 
                    ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-md' 
                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'
                }`}
              >
                Weekly
              </button>
              <button
                onClick={() => { setViewMode('monthly'); setOffset(0); }}
                data-testid="view-monthly-btn"
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                  viewMode === 'monthly' 
                    ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-md' 
                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200'
                }`}
              >
                Monthly
              </button>
            </div>
          </div>
        </div>

        {/* Period Navigation */}
        <div className="flex items-center justify-between mb-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={handlePrevious}
            data-testid="prev-period-btn"
            className="gap-1 text-slate-600 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400"
          >
            <ChevronLeft className="w-4 h-4" />
            Previous
          </Button>
          
          <span className="text-sm font-medium text-slate-700 dark:text-slate-300" data-testid="period-label">
            {getPeriodLabel()}
          </span>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={handleNext}
            disabled={offset === 0}
            data-testid="next-period-btn"
            className="gap-1 text-slate-600 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 disabled:opacity-50"
          >
            Next
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
        
        {chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={250}>
            {viewMode === 'weekly' ? (
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E0E7FF" />
                <XAxis 
                  dataKey="shortLabel" 
                  stroke="#94A3B8"
                  style={{ fontSize: '12px' }}
                />
                <YAxis 
                  stroke="#94A3B8"
                  style={{ fontSize: '12px' }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Line 
                  type="monotone" 
                  dataKey="completions" 
                  stroke="url(#colorGradient)"
                  strokeWidth={3}
                  dot={{ fill: '#3B82F6', strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6 }}
                />
                <defs>
                  <linearGradient id="colorGradient" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#3B82F6" />
                    <stop offset="100%" stopColor="#8B5CF6" />
                  </linearGradient>
                </defs>
              </LineChart>
            ) : (
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E0E7FF" />
                <XAxis 
                  dataKey="shortLabel" 
                  stroke="#94A3B8"
                  style={{ fontSize: '10px' }}
                  interval={2}
                />
                <YAxis 
                  stroke="#94A3B8"
                  style={{ fontSize: '12px' }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar 
                  dataKey="completions" 
                  fill="url(#barGradient)"
                  radius={[4, 4, 0, 0]}
                />
                <defs>
                  <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3B82F6" />
                    <stop offset="100%" stopColor="#8B5CF6" />
                  </linearGradient>
                </defs>
              </BarChart>
            )}
          </ResponsiveContainer>
        ) : (
          <div className="h-64 flex items-center justify-center text-stone-500 dark:text-stone-400">
            No activity data yet
          </div>
        )}
      </div>

      {/* Habit Performance */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <Award className="w-5 h-5 text-purple-600 dark:text-purple-400" />
          <h3 className="font-heading text-xl font-semibold text-slate-800 dark:text-slate-100">
            Habit Performance
          </h3>
        </div>

        {analytics.length === 0 ? (
          <div className="gradient-card rounded-2xl border border-white/20 dark:border-white/10 p-12 text-center">
            <p className="text-slate-500 dark:text-slate-400">
              No habits to analyze. Add some habits to see your progress!
            </p>
          </div>
        ) : (
          <div className="grid gap-4" data-testid="habit-performance-list">
            {analytics.map((habit) => {
              const IconComponent = Icons[habit.icon] || Icons.Circle;
              
              return (
                <div
                  key={habit.id}
                  data-testid={`analytics-habit-${habit.id}`}
                  className="gradient-card rounded-2xl border border-white/20 dark:border-white/10 p-6 hover:shadow-xl hover-glow transition-all duration-300"
                >
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-12 h-12 rounded-xl flex items-center justify-center shadow-md"
                        style={{ background: `linear-gradient(135deg, ${habit.color}40, ${habit.color}80)` }}
                      >
                        <IconComponent className="w-6 h-6" style={{ color: habit.color }} />
                      </div>
                      <div>
                        <h4 className="font-medium text-lg text-slate-800 dark:text-slate-100">
                          {habit.name}
                        </h4>
                        {habit.category && (
                          <p className="text-sm text-slate-500 dark:text-slate-400">
                            {habit.category}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <p className="text-sm text-slate-500 dark:text-slate-400">Current Streak</p>
                      <p className="text-2xl font-heading font-bold text-slate-800 dark:text-slate-100">
                        {habit.streak} days
                      </p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm text-slate-500 dark:text-slate-400">Completion Rate (30d)</p>
                      <div className="flex items-end gap-2">
                        <p className="text-2xl font-heading font-bold text-slate-800 dark:text-slate-100">
                          {habit.completionRate}%
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className="mt-4">
                    <div className="w-full bg-slate-100 dark:bg-slate-700 rounded-full h-2.5">
                      <div
                        className="h-2.5 rounded-full transition-all duration-500"
                        style={{
                          width: `${habit.completionRate}%`,
                          backgroundColor: habit.color,
                        }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Analytics;
